/**
 * Runtime configuration.
 *
 * The bundle is built once and deployed everywhere, so anything deployment-scoped
 * arrives at container start via `/config.js` rather than at build time. Only
 * genuinely frontend-owned values live here; backend-owned limits the UI merely
 * mirrors (page sizes, import caps, trash retention) come from `/api/settings/`
 * instead, so the two services cannot drift.
 */

export interface RuntimeConfig {
  /** Origin the browser uses to reach the API. Empty means same-origin. */
  publicBackendUrl: string
  publicWebFrontendUrl: string
  mediaUrl: string
  /** Origin used when embedding a publicly shared view or form. */
  embeddedShareUrl: string
  cookiePrefix: string
  sameSiteCookie: 'lax' | 'strict' | 'none'
  /**
   * Suppressed the Vue app's probe for a misconfigured `PUBLIC_BACKEND_URL`.
   * Nothing reads it now: the sign-in page reports an unreachable backend from
   * a request that actually failed rather than from a hostname comparison, so
   * there is no false positive left to suppress. Kept until the compose files
   * are cleaned up at cutover, because removing it is operator-facing.
   */
  disablePublicUrlCheck: boolean
  /** Fetch files through XHR rather than a plain link, for cross-origin media. */
  downloadFileViaXhr: boolean
  featureFlags: string
  sentryDsn: string
  sentryEnvironment: string
  sentryTracesSampleRate: number
  posthogProjectApiKey: string
  posthogHost: string
  extraClientScriptUrls: string[]
  /** The lithos-idp realm issuer, whose origin serves the suite's cookie-consent banner. */
  idpIssuer: string
}

declare global {
  interface Window {
    __LITHOS_CONFIG__?: Partial<RuntimeConfig>
  }
}

const DEFAULTS: RuntimeConfig = {
  publicBackendUrl: '',
  publicWebFrontendUrl: '',
  mediaUrl: '',
  embeddedShareUrl: '',
  cookiePrefix: '',
  sameSiteCookie: 'lax',
  disablePublicUrlCheck: false,
  downloadFileViaXhr: false,
  featureFlags: '',
  sentryDsn: '',
  sentryEnvironment: '',
  sentryTracesSampleRate: 0,
  posthogProjectApiKey: '',
  posthogHost: '',
  extraClientScriptUrls: [],
  idpIssuer: '',
}

let cached: RuntimeConfig | null = null
let loaded = false

export function getRuntimeConfig(): RuntimeConfig {
  if (cached === null) {
    loaded = window.__LITHOS_CONFIG__ !== undefined
    cached = { ...DEFAULTS, ...(window.__LITHOS_CONFIG__ ?? {}) }
  }
  return cached
}

/**
 * Whether `/config.js` ran at all.
 *
 * The defaults make a missing config file indistinguishable from an empty one --
 * the application works either way, against its own origin -- which is exactly
 * right for running it and exactly wrong for diagnosing it. `/debug` is the one
 * caller: a script that never loaded means the proxy is not serving it, and no
 * amount of setting environment variables will change anything until it does.
 */
export function isRuntimeConfigLoaded(): boolean {
  getRuntimeConfig()
  return loaded
}

/**
 * Origin for API and websocket calls. Behind Caddy the API is same-origin, so an
 * unset `publicBackendUrl` is the normal single-domain deployment, not an error.
 */
export function backendUrl(): string {
  return getRuntimeConfig().publicBackendUrl || window.location.origin
}

export function webFrontendUrl(): string {
  return getRuntimeConfig().publicWebFrontendUrl || window.location.origin
}

/** Websocket origin, derived from the backend origin. */
export function websocketUrl(): string {
  return `${backendUrl().replace(/^http/, 'ws')}/ws/core/`
}

export function isFeatureEnabled(flag: string): boolean {
  const flags = getRuntimeConfig().featureFlags
  if (flags.trim() === '*') return true
  return flags
    .split(',')
    .map((f) => f.trim().toLowerCase())
    .includes(flag.toLowerCase())
}

/** Test seam: lets a spec install a config without touching module load order. */
export function __setRuntimeConfigForTests(config: Partial<RuntimeConfig> | null): void {
  cached = config === null ? null : { ...DEFAULTS, ...config }
  loaded = config !== null
}
